import * as THREE from 'three';
import '../ui/farm.css';
import { C } from '../core/palette.js';
import { box, cyl, sphere, textSprite } from '../core/meshkit.js';
import { BreadBoi } from '../entities/breadBoi.js';
import { Townsperson, breadSlice, stickLegs, checkNpcClick } from '../entities/townsfolk.js';
import { HUD } from '../ui/hud.js';
import { getCrumbs, addCrumbs, getInventory, addSeeds, spendSeeds, addCrops, spendCrops, getFarm, plantPlot, harvestPlot, setFlag, expandFarm, waterPlot } from '../core/save.js';
import { getHour, getDay, advanceTime, onTimeChange, getSeason } from '../core/world.js';
import { WeatherSystem, getWeatherState } from '../core/weather.js';
import { CROPS, CROP_IDS, cropStage, isReady } from '../core/crops.js';
import { questsForGiver, acceptQuest, turnIn, isReady as isQuestReady, objectiveProgress, QUESTS, flagObjective } from '../core/quests.js';

// Sunnybrook Farm — a crop-growing zone reached from the town square.
// Plant seeds in tilled plots, wait for them to grow, harvest, and deliver
// produce for quests. Expand the field by buying more land from Farmer Grain.

export function createFarmScene(){
  return () => new FarmScene();
}

const PLOT_SIZE = 1.6;
const PLOT_GAP = 0.3;

class FarmScene {
  constructor(){
    this.state = 'explore';
    this.panels = [];
    this.farmerTab = 'quests';

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0xB8E0A8);
    this.scene.fog = new THREE.Fog(0xB8E0A8, 24, 58);

    this.camera = new THREE.PerspectiveCamera(48, window.innerWidth/window.innerHeight, 0.1, 100);
    this.camDist = 16;
    this.camTarget = new THREE.Vector3(0, 1.5, 0);
    this.azimuth = 0; this.polar = 0.5;

    this._buildLights();
    this._buildGround();
    this._buildBarn();
    this._buildPlots();

    this.bread = new BreadBoi();
    this.bread.root.position.set(0, 0, 7);
    this.bread.root.rotation.y = Math.PI;
    this.scene.add(this.bread.root);

    this.farmer = this._makeFarmer();
    this.scene.add(this.farmer.root);

    this.weather = new WeatherSystem(this.scene, { center: new THREE.Vector3(0, 0, 0), radius: 26 });
    this._applyWeather();

    this.hotspots = [
      { pos: this.farmer.root.position.clone(), r: 2.5, label: '🗣 talk to Farmer Grain', action: () => this._talkToFarmer() },
      { pos: new THREE.Vector3(0, 0, 8.5), r: 2.4, label: '← back to Toast Town', action: () => this.manager.goTo('town', { from: 'farm' }) }
    ];
    this.nearSpot = null;
  }

  _makeFarmer(){
    const build = (g) => {
      breadSlice(g, C.crust, C.toast, 0.6, 1.1);
      sphere(.07, 0x2E2018, -.16, 1.18, .3, g, 8);
      sphere(.07, 0x2E2018,  .16, 1.18, .3, g, 8);
      // straw hat
      const hat = new THREE.Group(); hat.position.set(0, 1.72, 0); g.add(hat);
      cyl(.55, .55, .06, 0xE8D6AE, 0, 0, 0, hat, 18);
      cyl(.3, .32, .34, 0xE8D6AE, 0, .2, 0, hat, 16);
      // overalls
      box(.62, .7, .36, 0x3D5FC0, 0, .45, 0, g);
      box(.16, .7, .38, 0x3D5FC0, -.24, .45, 0, g);
      box(.16, .7, .38, 0x3D5FC0,  .24, .45, 0, g);
      stickLegs(g, .5);
      // pitchfork
      const fork = new THREE.Group(); fork.position.set(.55, .6, .2); fork.rotation.z = -.25; g.add(fork);
      cyl(.03, .03, 1.6, C.cocoa, 0, .8, 0, fork, 6);
      for (let i = -1; i <= 1; i++) cyl(.018, .018, .28, C.cocoa, i * .08, 1.68, 0, fork, 4);
    };
    return new Townsperson({
      name: 'Farmer Grain', line: 'nothing beats dirt under your crust.',
      pos: new THREE.Vector3(-5, 0, 3), facing: Math.PI * .35,
      wander: 0, speed: 0, bubbleY: 2.6, build
    });
  }

  _buildLights(){
    this.scene.add(new THREE.AmbientLight(0xFFF6E8, 0.7));
    const shift = { spring: { fog: 0xB8E0A8 }, summer: { fog: 0xB8E0A8 }, autumn: { fog: 0xD4C88A }, winter: { fog: 0xD8E4E0 } }[getSeason()];
    if (shift) this.scene.fog.color.setHex(shift.fog);
    const sun = new THREE.DirectionalLight(0xFFF2D8, 0.9);
    sun.position.set(8, 16, 8); sun.castShadow = true;
    sun.shadow.mapSize.set(2048, 2048);
    const s = 22;
    sun.shadow.camera.left = -s; sun.shadow.camera.right = s;
    sun.shadow.camera.top = s; sun.shadow.camera.bottom = -s;
    this.scene.add(sun);
  }

  _applyWeather(){
    const st = getWeatherState();
    this.weather.set(st.weather, st.season);
  }

  _buildGround(){
    const col = { spring: 0x8FD166, summer: 0x7FBF6A, autumn: 0xC9B866, winter: 0xD0E0D8 }[getSeason()] || 0x7FBF6A;
    const grass = new THREE.Mesh(new THREE.CircleGeometry(28, 48), new THREE.MeshLambertMaterial({ color: col }));
    grass.rotation.x = -Math.PI/2; grass.receiveShadow = true; this.scene.add(grass);

    // dirt path from town
    const path = new THREE.Mesh(new THREE.PlaneGeometry(3.5, 12), new THREE.MeshLambertMaterial({ color: 0xB08A55 }));
    path.rotation.x = -Math.PI/2; path.position.set(0, 0.01, 10); this.scene.add(path);
  }

  _buildBarn(){
    const g = new THREE.Group(); g.position.set(-6, 0, -5); this.scene.add(g);
    box(5, 4, 6, 0xB83C3C, 0, 2, 0, g);            // barn body
    box(5.4, .4, 6.4, 0x8A2E2E, 0, 4.2, 0, g);     // roof trim
    // gambrel roof
    box(5.6, .3, 6.6, 0x6E2424, 0, 4.5, 0, g);
    box(5.2, 1.2, 6.6, 0x8A2E2E, 0, 5.0, 0, g);
    box(2.2, 2.2, .2, 0x3E2018, 0, 2.0, 3.0, g);   // door
    const sign = textSprite('Sunnybrook Farm', 30, '#FFF4DF', 5.2, 1.1);
    sign.position.set(-6, 5.2, -5); this.scene.add(sign);
  }

  _buildPlots(){
    this.plotGroup = new THREE.Group(); this.scene.add(this.plotGroup);
    this.plotMeshes = []; // { index, group, cropMesh }
    this._refreshPlots();
  }

  _refreshPlots(){
    // clear existing plot visuals
    this.plotGroup.clear();
    this.plotMeshes = [];

    const farm = getFarm();
    const w = farm.width, h = farm.height;
    const offX = -((w * (PLOT_SIZE + PLOT_GAP)) - PLOT_GAP) / 2 + PLOT_SIZE / 2;
    const offZ = -((h * (PLOT_SIZE + PLOT_GAP)) - PLOT_GAP) / 2 + PLOT_SIZE / 2 - 1;

    for (let z = 0; z < h; z++){
      for (let x = 0; x < w; x++){
        const idx = z * w + x;
        const px = offX + x * (PLOT_SIZE + PLOT_GAP);
        const pz = offZ + z * (PLOT_SIZE + PLOT_GAP);

        const g = new THREE.Group(); g.position.set(px, 0, pz); this.plotGroup.add(g);
        // tilled soil
        box(PLOT_SIZE, .12, PLOT_SIZE, 0x5C4033, 0, .06, 0, g);
        // border boards
        box(PLOT_SIZE + .06, .08, .08, 0x4A332A, 0, .04, PLOT_SIZE/2, g);
        box(PLOT_SIZE + .06, .08, .08, 0x4A332A, 0, .04, -PLOT_SIZE/2, g);
        box(.08, .08, PLOT_SIZE, 0x4A332A, PLOT_SIZE/2, .04, 0, g);
        box(.08, .08, PLOT_SIZE, 0x4A332A, -PLOT_SIZE/2, .04, 0, g);

        const plot = farm.plots[idx] || null;
        const cropMesh = this._cropMesh(plot, idx);
        if (cropMesh) g.add(cropMesh);

        this.plotMeshes.push({ index: idx, group: g, cropMesh });
      }
    }
  }

  _cropMesh(plot, idx){
    if (!plot || !CROPS[plot.crop]) return null;
    const c = CROPS[plot.crop];
    const stage = cropStage(plot, getHour(), getDay());
    const g = new THREE.Group();
    if (stage === 0){
      // seed mound
      sphere(.12, 0x3E2B20, 0, .08, 0, g, 8);
    } else if (stage === 1){
      // sprout
      cyl(.04, .04, .35, 0x7FBF6A, 0, .2, 0, g, 6);
      sphere(.12, 0x8FD14F, 0, .42, 0, g, 8);
    } else if (stage === 2){
      // growing
      cyl(.06, .06, .7, 0x7FBF6A, 0, .4, 0, g, 6);
      sphere(.28, c.color, 0, .8, 0, g, 10);
      sphere(.18, c.color, .2, .7, .2, g, 8);
      sphere(.18, c.color, -.2, .65, -.15, g, 8);
    } else {
      // ready
      cyl(.08, .08, .95, 0x6E9C63, 0, .5, 0, g, 6);
      sphere(.4, c.color, 0, 1.0, 0, g, 12);
      sphere(.28, c.color, .25, .9, .2, g, 10);
      sphere(.28, c.color, -.25, .85, -.2, g, 10);
      // little sparkle ring when ready
      const ring = new THREE.Mesh(new THREE.TorusGeometry(.55, .03, 6, 18), new THREE.MeshBasicMaterial({ color: 0xF7C948, transparent: true, opacity: .6 }));
      ring.rotation.x = -Math.PI/2; ring.position.y = .05; g.add(ring);
    }
    return g;
  }

  // ---- lifecycle ----
  enter({ manager }){
    this.manager = manager;
    this.input = manager.input;
    this.input.useCamera(this.camera);
    this.input.polarRange = [0.3, 0.72];
    this.input.onTap = (ndc, ray) => this._tap(ray);
    this._unsubTime = onTimeChange(() => this._refreshPlots());
    this._refreshPlots();
    HUD.setSubtitle('sunnybrook farm');
    HUD.say('Welcome to the farm! Talk to Farmer Grain for seeds and quests, then click a plot to plant or harvest.');
    HUD.setHint('click to walk · click a tilled plot to plant/harvest · talk to Farmer Grain');
    HUD.hideHintSoon(9000);
  }

  exit(){ this.input.onTap = null; HUD.showEnter(null); HUD.say(''); this._clearPanels(); if (this._unsubTime){ this._unsubTime(); this._unsubTime = null; } }

  _tap(ray){
    // First check if Farmer Wheat was clicked
    if (checkNpcClick(ray, this.farmer, this.bread)) return;

    // Then check if a plot was clicked
    const plotHit = this._raycastPlot(ray);
    if (plotHit !== null){ this._interactPlot(plotHit); return; }

    // Otherwise walk
    const dir = ray.ray.direction;
    if (Math.abs(dir.y) < 1e-4) return;
    const t = -ray.ray.origin.y / dir.y;
    if (t <= 0) return;
    const p = ray.ray.origin.clone().addScaledVector(dir, t);
    const gp = new THREE.Vector3(p.x, 0, p.z);
    gp.x = Math.max(-14, Math.min(14, gp.x));
    gp.z = Math.max(-11, Math.min(14, gp.z));
    HUD.hideHint();
    this.bread.walkTo(gp);
  }

  _raycastPlot(ray){
    // Raycast against each plot's soil box (y ~ 0.06)
    const origin = ray.ray.origin.clone();
    const dir = ray.ray.direction.clone();
    // Intersect with ground plane y=0, then find nearest plot center
    if (Math.abs(dir.y) < 1e-4) return null;
    const t = -origin.y / dir.y;
    if (t <= 0) return null;
    const p = origin.addScaledVector(dir, t);
    let best = null, bd = Infinity;
    this.plotMeshes.forEach(pm => {
      const c = pm.group.position;
      const d = Math.hypot(p.x - c.x, p.z - c.z);
      if (d < PLOT_SIZE/2 && d < bd){ bd = d; best = pm.index; }
    });
    return best;
  }

  _interactPlot(idx){
    const farm = getFarm();
    const plot = farm.plots[idx];
    if (plot){
      if (isReady(plot, getHour(), getDay())){
        const crop = harvestPlot(idx);
        this._refreshPlots();
        this.bread.drawFace('yum');
        setFlag('harvestedCrop', true);
        flagObjective('farm-first-harvest', 'harvestedCrop');
        if (CROPS[crop].seasons?.includes(getSeason())){
          setFlag('harvestedInSeason', true);
          flagObjective('seasonal-harvest', 'harvestedInSeason');
        }
        HUD.say(`Harvested ${CROPS[crop].icon} ${CROPS[crop].name}!`);
        advanceTime(0.25);
      } else {
        this._showWaterDialog(idx);
      }
      return;
    }

    // empty plot — show seed picker
    this._showSeedPicker(idx);
  }

  _showWaterDialog(idx){
    this._clearPanels();
    const farm = getFarm();
    const plot = farm.plots[idx];
    const c = CROPS[plot.crop];
    const hoursLeft = Math.max(0, c.growTime - ((getDay() - plot.plantedDay) * 24 + (getHour() - plot.plantedHour) + (plot.watered || 0)));
    const p = this._panel(`
      <div class="cafe-card q-dialog">
        <h2>${c.icon} ${c.name}</h2>
        <div class="cafe-sub">about ${Math.ceil(hoursLeft)} hour${hoursLeft === 1 ? '' : 's'} until harvest</div>
        <p class="q-dnone">Watering gives the crop a 1-hour growth boost.</p>
        <button class="cafe-btn water">💧 water crop (15 min)</button>
        <button class="cafe-btn ghost close">never mind</button>
      </div>`);
    p.querySelector('.water').onclick = () => {
      waterPlot(idx);
      advanceTime(0.25);
      this._refreshPlots();
      this._clearPanels();
      HUD.say(`Watered ${c.icon} ${c.name}! It's growing faster now.`);
    };
    p.querySelector('.close').onclick = () => this._clearPanels();
  }

  _showSeedPicker(idx){
    this._clearPanels();
    const seeds = getInventory().seeds;
    const rows = CROP_IDS.map(id => {
      const c = CROPS[id];
      const have = seeds[id] || 0;
      return `<div class="q-drow"><div><b>${c.icon} ${c.name}</b><span>${c.desc}<br>have: ${have} seed${have === 1 ? '' : 's'} · grows in ${c.growTime}h</span></div>
        <button class="cafe-btn plant ${have > 0 ? '' : 'disabled'}" data-crop="${id}">${have > 0 ? 'plant' : 'no seeds'}</button></div>`;
    }).join('');

    const p = this._panel(`
      <div class="cafe-card q-dialog">
        <h2>Plant a seed</h2>
        <div class="q-drows">${rows}</div>
        <button class="cafe-btn ghost close">never mind</button>
      </div>`);
    p.querySelectorAll('.plant').forEach(b => b.onclick = () => this._plant(idx, b.dataset.crop));
    p.querySelector('.close').onclick = () => this._clearPanels();
  }

  _plant(idx, cropId){
    if (!spendSeeds(cropId, 1)){
      HUD.say(`You don't have any ${CROPS[cropId].name} seeds.`);
      return;
    }
    plantPlot(idx, cropId, getHour(), getDay());
    this._refreshPlots();
    this._clearPanels();
    this.bread.drawFace('grin');
    HUD.say(`Planted ${CROPS[cropId].icon} ${CROPS[cropId].name}!`);
    advanceTime(0.5);
  }

  // ---- Farmer Grain: seeds, shop, quests ----
  _talkToFarmer(){
    this.bread.stop();
    const to = new THREE.Vector3().subVectors(this.farmer.root.position, this.bread.root.position);
    this.bread.facing = Math.atan2(to.x, to.z);
    this.bread.root.rotation.y = this.bread.facing;
    this.bread.drawFace('grin');
    this._renderFarmerDialog();
  }

  _renderFarmerDialog(){
    this._clearPanels();
    const { available, active } = questsForGiver('farmer');
    const questRows = [];
    active.forEach(id => {
      const q = QUESTS[id];
      if (isQuestReady(id)){
        questRows.push(`<div class="q-drow ready"><div><b>${q.title}</b><span>objective complete!</span></div>
          <button class="cafe-btn turnin" data-id="${id}">turn in ✓</button></div>`);
      } else {
        const p = objectiveProgress(id);
        const label = q.objective.kind === 'flag' ? q.objective.noun : `${p.have} / ${p.goal} ${q.objective.noun}`;
        questRows.push(`<div class="q-drow"><div><b>${q.title}</b><span>${label}</span></div><span class="q-dpend">in progress</span></div>`);
      }
    });
    available.forEach(id => {
      const q = QUESTS[id];
      const rew = q.reward?.crumbs ? ` · reward 🍞 ${q.reward.crumbs}` : '';
      questRows.push(`<div class="q-drow"><div><b>${q.title}</b><span>${q.desc}${rew}</span></div>
        <button class="cafe-btn accept" data-id="${id}">accept</button></div>`);
    });
    const questBody = questRows.length ? questRows.join('') : '<p class="q-dnone">"No deliveries needed right now."</p>';

    const seedRows = CROP_IDS.map(id => {
      const c = CROPS[id];
      const affordable = getCrumbs() >= c.seedPrice;
      return `<div class="q-drow"><div><b>${c.icon} ${c.name} seeds</b><span>${c.desc}<br>🍞 ${c.seedPrice} each · crop sells for 🍞 ${c.sellPrice}</span></div>
        <button class="cafe-btn buy ${affordable ? '' : 'disabled'}" data-crop="${id}">buy</button></div>`;
    }).join('');
    const crops = getInventory().crops;
    const farm = getFarm();
    const expandCost = 100;
    const canExpandW = farm.width < 6;
    const canExpandH = farm.height < 6;
    const expandRows = `
      <div class="q-drow"><div><b>➡️ expand field width</b><span>add a column of plots · 🍞 ${expandCost}</span></div>
        <button class="cafe-btn expand ${canExpandW && getCrumbs() >= expandCost ? '' : 'disabled'}" data-dir="width">buy</button></div>
      <div class="q-drow"><div><b>⬇️ expand field length</b><span>add a row of plots · 🍞 ${expandCost}</span></div>
        <button class="cafe-btn expand ${canExpandH && getCrumbs() >= expandCost ? '' : 'disabled'}" data-dir="height">buy</button></div>`;
    const sellRows = CROP_IDS.map(id => {
      const c = CROPS[id];
      const have = crops[id] || 0;
      return `<div class="q-drow"><div><b>${c.icon} ${c.name}</b><span>you have: ${have} · sell price: 🍞 ${c.sellPrice}</span></div>
        <button class="cafe-btn sell ${have > 0 ? '' : 'disabled'}" data-crop="${id}">sell</button></div>`;
    }).join('');

    const p = this._panel(`
      <div class="cafe-card q-dialog">
        <h2>Farmer Grain</h2>
        <div class="cafe-sub">sunnybrook farm</div>
        <div class="q-tabs">
          <button class="cafe-btn ghost ${this.farmerTab === 'quests' ? 'active' : ''}" data-tab="quests">📜 deliveries</button>
          <button class="cafe-btn ghost ${this.farmerTab === 'market' ? 'active' : ''}" data-tab="market">🌱 market</button>
        </div>
        <div class="q-tab-body">${this.farmerTab === 'quests' ? questBody : `<div class="q-drows">${seedRows}${sellRows}</div>`}</div>
        <button class="cafe-btn ghost close">done</button>
      </div>`);
    p.querySelectorAll('[data-tab]').forEach(b => b.onclick = () => { this.farmerTab = b.dataset.tab; this._renderFarmerDialog(); });
    p.querySelectorAll('.accept').forEach(b => b.onclick = () => { acceptQuest(b.dataset.id); this.bread.drawFace('yum'); this._renderFarmerDialog(); });
    p.querySelectorAll('.turnin').forEach(b => b.onclick = () => this._onTurnIn(b.dataset.id));
    p.querySelectorAll('.buy').forEach(b => b.onclick = () => this._buySeed(b.dataset.crop));
    p.querySelectorAll('.sell').forEach(b => b.onclick = () => this._sellCrop(b.dataset.crop));
    p.querySelectorAll('.expand').forEach(b => b.onclick = () => this._expandFarm(b.dataset.dir));
    p.querySelector('.close').onclick = () => this._clearPanels();
  }

  _buySeed(id){
    const c = CROPS[id];
    if (getCrumbs() < c.seedPrice){
      HUD.say(`You need ${c.seedPrice} crumbs for ${c.name} seeds.`);
      return;
    }
    addCrumbs(-c.seedPrice);
    addSeeds(id, 1);
    HUD.refreshCrumbs();
    this._renderFarmerDialog();
    HUD.say(`Bought ${c.icon} ${c.name} seeds!`);
  }

  _sellCrop(id){
    const c = CROPS[id];
    if (!spendCrops(id, 1)){
      HUD.say(`You don't have any ${c.name} to sell.`);
      return;
    }
    addCrumbs(c.sellPrice);
    HUD.refreshCrumbs();
    this._renderFarmerDialog();
    HUD.say(`Sold ${c.icon} ${c.name} for ${c.sellPrice} crumbs!`);
  }

  _expandFarm(dir){
    const cost = 100;
    if (getCrumbs() < cost){
      HUD.say(`You need ${cost} crumbs to expand the farm.`);
      return;
    }
    addCrumbs(-cost);
    expandFarm(dir);
    HUD.refreshCrumbs();
    this._refreshPlots();
    this._renderFarmerDialog();
    HUD.say(`Farm expanded! You now have more plots to plant.`);
  }

  _onTurnIn(id){
    const reward = turnIn(id);
    if (!reward) return;
    HUD.refreshCrumbs();
    HUD.refreshLumber();
    this.bread.drawFace('yum');
    this._renderFarmerDialog();
  }

  _panel(html, bare){
    const el = document.createElement('div');
    el.className = bare ? '' : 'cafe-panel';
    el.innerHTML = html;
    document.body.appendChild(el);
    this.panels.push(el);
    return el;
  }
  _clearPanels(){ this.panels.forEach(p => p.remove()); this.panels = []; }

  update(dt){
    this.farmer.update(dt, this.bread.root.position);
    this.weather.update(dt, this.bread.root.position);
    this.bread.update(dt);

    let near = null, nd = Infinity;
    this.hotspots.forEach(h => { const d = h.pos.distanceTo(this.bread.root.position); if (d < nd){ nd = d; near = h; } });
    if (near && nd < near.r){
      if (this.nearSpot !== near){ this.nearSpot = near; HUD.showEnter(near.label, () => near.action()); }
    } else if (this.nearSpot){ this.nearSpot = null; HUD.showEnter(null); }

    this.azimuth += (this.input.azimuthGoal - this.azimuth) * 0.08;
    this.polar   += (this.input.polarGoal   - this.polar)   * 0.08;
    this.camTarget.lerp(new THREE.Vector3(this.bread.root.position.x * 0.5, 1.6, this.bread.root.position.z * 0.5), 0.06);
    this.camera.position.set(
      this.camTarget.x + this.camDist * Math.sin(this.azimuth) * Math.cos(this.polar),
      this.camTarget.y + this.camDist * Math.sin(this.polar),
      this.camTarget.z + this.camDist * Math.cos(this.azimuth) * Math.cos(this.polar)
    );
    this.camera.lookAt(this.camTarget);
  }

  onResize(){
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
  }
}
